import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { ArrowIcon } from "../common/Section";
import { resolveImage } from "../admin/ImageUpload";
import YouTubeModal from "../common/YouTubeModal";

/** Extract YouTube video id from any common URL shape. */
const extractYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
};

const HeroSlider = ({ slides = [], fallbackHero, settings }) => {
  const { pick } = useI18n();
  // Clamp helper: settings values are 0-100, converted to CSS opacity 0-1
  const pct = (v, fb) => {
    const n = typeof v === "number" ? v : fb;
    return Math.max(0, Math.min(100, n)) / 100;
  };
  const overlayA   = pct(settings?.hero_overlay_opacity,        60);
  const grainA     = pct(settings?.hero_grain_opacity,          15);
  const gradientA  = pct(settings?.hero_bottom_gradient_opacity,40);
  const vignetteA  = pct(settings?.hero_vignette_opacity,       30);
  const [idx, setIdx] = useState(0);
  const [modalUrl, setModalUrl] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const list = slides.length ? slides : (fallbackHero ? [fallbackHero] : []);

  if (!list.length) return null;
  const s = list[idx];

  const videoSrc = (isMobile && s.background_video_mobile) ? s.background_video_mobile : s.background_video;
  const posterSrc = resolveImage(s.background_image);
  const titleLines = (pick(s.title, "") || "").split("\n");

  const openYoutube = (e) => {
    e.preventDefault();
    setModalUrl(s.youtube_url);
  };

  return (
    <>
      <section
        className="relative h-[100svh] min-h-[640px] w-full overflow-hidden flex items-end"
        style={{ background: "linear-gradient(135deg, #101F4A 0%, #22346B 55%, #1A2A5A 100%)" }}
        data-testid="home-hero-slider"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.19, 1, 0.22, 1] }}
            className="absolute inset-0"
          >
            {(() => {
              const ytId = extractYouTubeId(videoSrc);
              if (ytId) {
                const params = new URLSearchParams({
                  autoplay: "1",
                  mute: "1",
                  loop: "1",
                  playlist: ytId,
                  controls: "0",
                  showinfo: "0",
                  modestbranding: "1",
                  rel: "0",
                  iv_load_policy: "3",
                  disablekb: "1",
                  playsinline: "1",
                });
                return (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {/* Always render the poster/gradient underneath the iframe so that if
                        the YouTube video is unavailable, private or blocked, the user
                        still sees a proper hero background instead of a "Video unavailable" screen. */}
                    {posterSrc ? (
                      <img
                        src={posterSrc}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                        data-testid={`hero-yt-fallback-${idx}`}
                      />
                    ) : (
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            "linear-gradient(135deg, #101F4A 0%, #22346B 55%, #1A2A5A 100%)",
                        }}
                      />
                    )}
                    <iframe
                      key={`${idx}-${ytId}`}
                      title={`hero-yt-${idx}`}
                      src={`https://www.youtube-nocookie.com/embed/${ytId}?${params.toString()}`}
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      frameBorder="0"
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] h-[56.25vw] min-w-full min-h-full"
                      data-testid={`hero-yt-${idx}`}
                    />
                  </div>
                );
              }
              if (videoSrc) {
                return (
                  <video
                    key={`${idx}-${videoSrc}`}
                    src={resolveImage(videoSrc)}
                    poster={posterSrc}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    data-testid={`hero-video-${idx}`}
                  />
                );
              }
              return (
                <img
                  src={posterSrc}
                  alt=""
                  className="w-full h-full object-cover"
                  data-testid={`hero-image-${idx}`}
                />
              );
            })()}
          </motion.div>
        </AnimatePresence>

        {/* Configurable overlays — controlled from Site Settings.
            All four are optional; setting opacity to 0 removes the layer entirely. */}
        {overlayA > 0 ? (
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background: `linear-gradient(180deg, rgba(29,42,82,${overlayA * 0.6}) 0%, rgba(29,42,82,${overlayA * 0.85}) 50%, rgba(29,42,82,${overlayA}) 100%)`,
            }}
            data-testid="hero-overlay"
          />
        ) : null}
        {grainA > 0 ? (
          <div
            className="absolute inset-0 pointer-events-none z-[2]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E")`,
              mixBlendMode: "overlay",
              opacity: grainA,
            }}
            data-testid="hero-grain"
          />
        ) : null}
        {gradientA > 0 ? (
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background: `linear-gradient(to top, rgba(0,0,0,${gradientA}) 0%, rgba(0,0,0,${gradientA * 0.35}) 40%, rgba(0,0,0,${gradientA * 0.55}) 100%)`,
            }}
            data-testid="hero-bottom-gradient"
          />
        ) : null}
        {vignetteA > 0 ? (
          <div
            className="absolute inset-0 pointer-events-none z-[1]"
            style={{
              background: `radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,${vignetteA}) 100%)`,
            }}
            data-testid="hero-vignette"
          />
        ) : null}

        <div className="relative z-10 container-x pb-32 md:pb-40 pt-32 md:pt-40 w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={`content-${idx}`}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="max-w-6xl"
            >
              {pick(s.kicker) ? (
                <div className="flex items-center gap-3 mb-6" data-testid="hero-kicker">
                  <span className="h-px w-12 bg-white/70" />
                  <span className="text-[0.7rem] md:text-[0.75rem] tracking-[0.36em] uppercase text-white/85 font-medium">
                    {pick(s.kicker)}
                  </span>
                </div>
              ) : null}
              <h1
                className="font-serif text-white leading-[0.95] tracking-tight"
                style={{ fontSize: "clamp(2.75rem, 8vw, 8rem)", whiteSpace: "pre-line" }}
                data-testid="hero-title"
              >
                {titleLines.map((line, i) => (
                  <span key={i} className="block">
                    {line}
                  </span>
                ))}
              </h1>
              {pick(s.subtitle) ? (
                <p
                  className="mt-8 md:mt-10 max-w-2xl text-white/85 leading-relaxed"
                  style={{ fontSize: "clamp(1rem, 1.2vw, 1.15rem)" }}
                  data-testid="hero-subtitle"
                >
                  {pick(s.subtitle)}
                </p>
              ) : null}
              {pick(s.cta_label) ? (
                <div className="mt-10 md:mt-12">
                  {s.youtube_url ? (
                    <button
                      type="button"
                      onClick={openYoutube}
                      className="group inline-flex items-center gap-4 pl-2 pr-6 py-3 border border-white/60 text-white text-[0.72rem] tracking-[0.28em] uppercase font-medium transition-all duration-500 hover:bg-white hover:text-brand-navy hover:border-white"
                      data-testid="hero-video-cta"
                    >
                      <span className="w-9 h-9 rounded-full bg-white/15 border border-white/30 flex items-center justify-center group-hover:bg-brand-navy group-hover:border-brand-navy transition-all duration-500">
                        <Play size={12} strokeWidth={1.5} fill="currentColor" className="text-white group-hover:text-white" />
                      </span>
                      <span>{pick(s.cta_label)}</span>
                    </button>
                  ) : (
                    <Link to={s.cta_url || "/"} className="btn-primary" data-testid="hero-cta">
                      <span>{pick(s.cta_label)}</span>
                      <ArrowIcon />
                    </Link>
                  )}
                </div>
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>

        {list.length > 1 ? (
          <div
            className="absolute left-1/2 -translate-x-1/2 bottom-14 md:bottom-16 z-10 flex items-center gap-5"
            data-testid="hero-dots"
            dir="ltr"
          >
            <span className="text-white/90 text-[0.7rem] tracking-[0.32em] font-medium tabular-nums">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <span className="h-px w-8 bg-white/40" />
            <div className="flex items-center gap-2.5">
              {list.map((_, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`hero-dot-${i}`}
                  className={`rounded-full transition-all duration-500 ${
                    i === idx ? "w-9 h-2 bg-white" : "w-2 h-2 bg-white/40 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
            <span className="h-px w-8 bg-white/40" />
            <span className="text-white/60 text-[0.7rem] tracking-[0.32em] font-medium tabular-nums">
              {String(list.length).padStart(2, "0")}
            </span>
          </div>
        ) : null}

        <div className="absolute bottom-6 right-6 md:bottom-8 md:right-12 z-10 text-white/85 flex items-center gap-3 text-[0.68rem] tracking-[0.32em] uppercase">
          <span>Scroll</span>
          <span className="block w-8 h-px bg-white/60" />
        </div>
      </section>

      <YouTubeModal url={modalUrl} onClose={() => setModalUrl(null)} />
    </>
  );
};

export default HeroSlider;
